import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, Checkbox } from '@mui/material';
import { Autocomplete, TextField, IconButton } from '@totalsoft/rocket-ui';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { Close, Check } from '@mui/icons-material';
import { over, get, set } from '@totalsoft/change-tracking-react';
import { StyledTableRow, StyledTableBodyCell } from 'components/common/Styles';
import { useFieldDecimalPlacesConfig } from 'hooks';

const FinancialProductRulesEditDetailsIncomesTableRow = ({
  canWrite,
  incomeLens,
  index,
  onCloseEdit,
  onDelete,
  onSave,
  financialProductRuleIncomeCurrencyList,
  financialProductRuleIncomeTypeList,
  financialProductRuleIncomeValidation,
  listValid
}) => {
  const [t] = useTranslation();
  const income = incomeLens |> get;
  const { cvsChAmount } = useFieldDecimalPlacesConfig();

  return (
    <StyledTableRow>
      {income.isInEditing ? (
        <>
          <StyledTableBodyCell>
            <Checkbox
              checked={income.readOnlyYN || false}
              onChange={() => set(incomeLens.readOnlyYN)(!income.readOnlyYN)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <Autocomplete
              valueKey={'incomeTypeId'}
              labelKey={'incomeTypeName'}
              value={ income?.incomeTypeId !== null ? { incomeTypeId: income?.incomeTypeId, incomeTypeName: income?.incomeTypeName } : null}
              options={financialProductRuleIncomeTypeList}
              onChange={event => {
                over(incomeLens, oldState => ({
                  ...oldState,
                  incomeTypeId: event.incomeTypeId,
                  incomeTypeName: event.incomeTypeName
                }));
              }}
              error={!isValid(financialProductRuleIncomeValidation.incomeTypeId)}
              helperText={getErrors(financialProductRuleIncomeValidation.incomeTypeId)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <TextField
              type='number'
              defaultValue={income.value}
              variant='standard'
              onChange={event => {
                over(incomeLens, oldState => ({ ...oldState, value: event }));
              }}
              error={!isValid(financialProductRuleIncomeValidation.value)}
              helperText={getErrors(financialProductRuleIncomeValidation.value)}
              decimalScale={cvsChAmount}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <Autocomplete
              valueKey={'id'}
              labelKey={'code'}
              value={ income?.currencyId !== null ? { id: income?.currencyId, code: income?.currencyCode } : null}
              options={financialProductRuleIncomeCurrencyList}
              onChange={event => {
                over(incomeLens, oldState => ({
                  ...oldState,
                  currencyId: event.id,
                  currencyCode: event.code
                }));
              }}
              error={!isValid(financialProductRuleIncomeValidation.currencyId)}
              helperText={getErrors(financialProductRuleIncomeValidation.currencyId)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <Checkbox
              checked={income.implicit || false}
              onChange={() => set(incomeLens.implicit)(!income.implicit)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <Checkbox
              checked={income.mandatory || false}
              onChange={() => set(incomeLens.mandatory)(!income.mandatory)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell align='right'>
            <Grid container wrap='nowrap' justifyContent='flex-end'>
              <IconButton
                type='save'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.Save')}
                disabled={income.isSaving || !canWrite || !listValid}
                onClick={() => onSave(index)}
              />
              <IconButton
                type='cancel'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.CloseEdit')}
                onClick={() => onCloseEdit(index)}
              />
            </Grid>
          </StyledTableBodyCell>
        </>
      ) : (
        <>
          <StyledTableBodyCell>
            <>{(income.readOnlyYN) === true ? <Check color='success' /> : <Close />}</>
          </StyledTableBodyCell>
          <StyledTableBodyCell>{income.incomeTypeName}</StyledTableBodyCell>
          <StyledTableBodyCell>{income.value}</StyledTableBodyCell>
          <StyledTableBodyCell>{income.currencyCode}</StyledTableBodyCell>
          <StyledTableBodyCell>
            <>{(income.implicit) === true ? <Check color='success' /> : <Close />}</>
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <>{(income.mandatory) === true ? <Check color='success' /> : <Close />}</>
          </StyledTableBodyCell>
          <StyledTableBodyCell align='right'>
            <Grid container wrap='nowrap' justifyContent='flex-end'>
              <IconButton
                type='edit'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.Edit')}
                onClick={() => set(incomeLens.isInEditing)(true)}
                disabled={!canWrite} />
              <IconButton
                type='delete'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.Delete')}
                onClick={() => onDelete(income.financialProductRuleIncomeId, index)}
                disabled={!canWrite}
              />
            </Grid>
          </StyledTableBodyCell>
        </>
      )}
    </StyledTableRow>
  );
};

FinancialProductRulesEditDetailsIncomesTableRow.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  incomeLens: PropTypes.object,
  index: PropTypes.number,
  onCloseEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  financialProductRuleIncomeCurrencyList: PropTypes.array.isRequired,
  financialProductRuleIncomeTypeList: PropTypes.array.isRequired,
  financialProductRuleIncomeValidation: PropTypes.object.isRequired,
  listValid: PropTypes.bool.isRequired
};

export default FinancialProductRulesEditDetailsIncomesTableRow;
